// The grid editor's logic (pure; ColumnGrid.jsx renders it). One draft per column of a file;
// a draft is what the cells show, `patchOf` turns it back into the changed fields of a
// ColumnPatch, `validateDraft` applies the server's rules as you type (backend
// routers/columns.py ColumnPatch + unio/validation/constraints.py + services/file_refs.py), and
// `applyPaste` fills a block of cells from TSV copied out of a spreadsheet.
//
// The batch save is PATCH /api/files/{id}/columns [{id, ...patch}], answered 200 [ColumnOut]
// or 422 {"detail": [{loc: ["body", <item>, <field>], msg}]}; `serverCellErrors` maps those
// onto (row, grid field).
import { ENUM_TYPES, FIELD_TYPES, parseMissing, RANGE_TYPES, TEXT_TYPES } from "./terms";

/** The editable cells of a row, in grid order (the column name is the row header before them). */
export const GRID_FIELDS = [
  { key: "type", label: "Type" },
  { key: "format", label: "Format" },
  { key: "title", label: "Title" },
  { key: "description", label: "Description" },
  { key: "missing", label: "Missing values" },
  { key: "required", label: "Required" },
  { key: "unique", label: "Unique" },
];

// Header cells a pasted block may start with (lower case) -> grid field ("column" = the name).
const HEADERS = {
  column: "column", name: "column", "column name": "column", field: "column",
  type: "type", format: "format", title: "title", description: "description",
  "missing values": "missing", missing_values: "missing", missing: "missing",
  "missing-value markers": "missing", required: "required", unique: "unique",
};

const LIMITS = { format: 64, title: 255, description: 10000 };

/** ColumnOut -> the cells of its row. */
export function draftOf(col) {
  const cons = col.constraints || {};
  return {
    type: col.type || "string",
    format: col.format && col.format !== "default" ? col.format : "",
    title: col.title || "",
    description: col.description || "",
    missing: (col.missing_values || []).join(", "),
    required: !!cons.required,
    unique: !!cons.unique,
  };
}

/** "yes" / "no" and friends -> boolean; anything else is kept as text (and shown as an error). */
export function parseBool(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (["yes", "y", "true", "1", "x", "✓", "required", "unique"].includes(s)) return true;
  if (["no", "n", "false", "0", ""].includes(s)) return false;
  return String(raw).trim();
}

/** Tab-separated text as spreadsheets put it on the clipboard: rows of cells. A cell with a tab,
 *  newline or quote comes quoted ("a ""b"""). A trailing newline does not add a row. */
export function parseTsv(text) {
  const rows = [];
  let row = [], cell = "", i = 0, quoted = false;
  const s = String(text || "");
  while (i < s.length) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"' && s[i + 1] === '"') { cell += '"'; i += 2; continue; }
      if (ch === '"') { quoted = false; i += 1; continue; }
      cell += ch; i += 1; continue;
    }
    if (ch === '"' && cell === "") { quoted = true; i += 1; continue; }
    if (ch === "\t") { row.push(cell); cell = ""; i += 1; continue; }
    if (ch === "\r" || ch === "\n") {
      row.push(cell); rows.push(row); row = []; cell = "";
      i += ch === "\r" && s[i + 1] === "\n" ? 2 : 1;
      continue;
    }
    cell += ch; i += 1;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

/** One cell set from pasted text. */
export function setFromText(draft, key, raw) {
  const text = String(raw ?? "");
  switch (key) {
    case "type": {
      const t = text.trim().toLowerCase();
      return t ? { ...draft, type: t } : draft;            // a blank type cell leaves the type as is
    }
    case "required": case "unique": return { ...draft, [key]: parseBool(text) };
    case "missing": return { ...draft, missing: text.trim() };
    default: return { ...draft, [key]: text.trim() };
  }
}

/** Paste `text` (TSV) at row `row`, field `field` ("column" = the row header: the first pasted
 *  column then names the rows). A first line of known headers (Column, Type, Title, ...) maps the
 *  pasted columns by header instead. -> {drafts, touched: Set of row indexes, messages: [text]} */
export function applyPaste(drafts, columns, row, field, text) {
  let lines = parseTsv(text);
  const messages = [];
  if (!lines.length) return { drafts, touched: new Set(), messages };
  let fields;
  const head = lines[0].map(c => HEADERS[c.trim().toLowerCase()]);
  // a header line names at least two columns: one pasted cell ("Type") is a value, not a header
  if (lines[0].filter(c => c.trim()).length >= 2 && lines[0].every((c, i) => !c.trim() || head[i])) {
    fields = head;
    lines = lines.slice(1);
  } else {
    const keys = ["column", ...GRID_FIELDS.map(f => f.key)];
    const start = Math.max(0, keys.indexOf(field));
    const width = Math.max(...lines.map(l => l.length));
    fields = Array.from({ length: width }, (_, i) => keys[start + i]);
    if (width + start > keys.length) {
      messages.push(`${width + start - keys.length} pasted column(s) did not fit to the right and were left out.`);
    }
  }
  const byName = new Map(columns.map((c, i) => [c.name, i]));
  const nameAt = fields.indexOf("column");
  const out = drafts.slice();
  const touched = new Set();
  const unknown = [];
  let overflow = 0;
  lines.forEach((cells, n) => {
    let target;
    if (nameAt >= 0) {
      const name = (cells[nameAt] || "").trim();
      if (!byName.has(name)) { if (name) unknown.push(name); return; }
      target = byName.get(name);
    } else {
      target = row + n;
      if (target >= out.length) { overflow += 1; return; }
    }
    let d = out[target];
    fields.forEach((key, i) => {
      if (key && key !== "column" && i < cells.length) d = setFromText(d, key, cells[i]);
    });
    out[target] = d;
    touched.add(target);
  });
  if (unknown.length) messages.push(`No column named ${unknown.join(", ")} in this file; those lines were left out.`);
  if (overflow) messages.push(`${overflow} pasted line(s) went past the last column and were left out.`);
  return { drafts: out, touched, messages };
}

function fits(rule, type) {
  if (rule === "minimum" || rule === "maximum") return RANGE_TYPES.includes(type);
  if (["minLength", "maxLength", "pattern"].includes(rule)) return TEXT_TYPES.includes(type);
  if (rule === "enum") return ENUM_TYPES.includes(type);
  return true;
}

const tooLong = (s, n) => String(s || "").trim().length > n ? `At most ${n} characters.` : null;

/** The cell errors of a row: {field: message}, the same rules the server applies. */
export function validateDraft(draft, col) {
  const e = {};
  if (!FIELD_TYPES.includes(draft.type)) e.type = `“${draft.type}” is not a type: use one of ${FIELD_TYPES.join(", ")}.`;
  else {
    // the server checks the stored rules only when type or rules are sent (a rule kept from an
    // earlier type, e.g. after re-infer, does not block a title edit)
    const was = draftOf(col);
    const sendsRules = draft.type !== was.type || draft.required !== was.required || draft.unique !== was.unique;
    const misfit = sendsRules && Object.keys(col.constraints || {}).find(k => !fits(k, draft.type));
    if (misfit) {
      e.type = `The rule ${misfit} does not fit ${draft.type}: change or remove it in the entry editor first.`;
    } else if (col.logical_type === "file" && !TEXT_TYPES.includes(draft.type)) {
      e.type = `A ${col.logical_type} column holds names: its type must be string, not ${draft.type}.`;
    }
  }
  Object.entries(LIMITS).forEach(([k, n]) => { const m = tooLong(draft[k], n); if (m) e[k] = m; });
  const markers = parseMissing(draft.missing);
  if (markers.length > 50) e.missing = "At most 50 markers.";
  else if (markers.some(m => m.length > 100)) e.missing = "A marker is at most 100 characters.";
  ["required", "unique"].forEach(k => {
    if (typeof draft[k] !== "boolean") e[k] = `“${draft[k]}” is not yes or no.`;
  });
  return e;
}

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** The changed fields of a row as a ColumnPatch (with confirmed: true), or null if unchanged. */
export function patchOf(col, draft) {
  const was = draftOf(col);
  const p = {};
  if (draft.type !== was.type) p.type = draft.type;
  if (draft.format.trim() !== was.format) p.format = draft.format.trim() || "default";
  if (draft.title.trim() !== was.title) p.title = draft.title.trim() || null;
  if (draft.description.trim() !== was.description) p.description = draft.description.trim() || null;
  const markers = parseMissing(draft.missing);
  if (!same(markers, col.missing_values || [])) p.missing_values = markers;
  if (draft.required !== was.required || draft.unique !== was.unique) {
    const cons = { ...(col.constraints || {}) };
    delete cons.required; delete cons.unique;
    if (draft.required === true) cons.required = true;
    if (draft.unique === true) cons.unique = true;
    p.constraints = cons;
  }
  if (!Object.keys(p).length) return null;
  p.confirmed = true;
  return p;
}

// A field of ColumnPatch -> the grid cell that shows it.
const FIELD_OF = {
  type: "type", constraints: "type", logical_type: "type", format: "format", title: "title",
  description: "description", missing_values: "missing", id: "column",
  confirmed: "column",
};

/** A 422 of the batch -> {rowIndex: {field: message}}; `sent` = the grid row index of each item. */
export function serverCellErrors(detail, sent) {
  const out = {};
  (Array.isArray(detail) ? detail : []).forEach(d => {
    const loc = d.loc || [];
    const item = loc[1];
    if (loc[0] !== "body" || typeof item !== "number" || sent[item] === undefined) return;
    const field = FIELD_OF[loc[2]] || "column";
    const row = sent[item];
    out[row] = { ...(out[row] || {}), [field]: String(d.msg || "").replace(/^Value error, /, "") };
  });
  return out;
}
